import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { getValue, unwrapParam } from "../../lib/vociSchema";
import { PARAM_LABELS } from "../../lib/kb";

/**
 * R11.b — Learning rule create modal (multi-step).
 *
 * Steps:
 *   1) Cosa correggere     — table P-fields with checkbox + new value
 *   2) Scope               — radio: articolo_listino | cliente | settore
 *   3) Motivazione         — required textarea
 *   4) Preview             — show the rule that will be created
 *
 * Submits POST /api/learning-row-rules.
 * Created rules:
 *   - super_admin + titolare → status=active
 *   - commercial → status=pending_approval
 */
const PARAM_CANDIDATES = [
  "P01_DN", "P02_connessione", "P06_PN", "P10_materiale",
  "P11_tenuta", "P13_tipo_otturatore", "P16_azionamento",
  "P18_fluido", "P21_settore", "P28_tipo_raccordo", "P29_raggio_curva",
  "P30_spessore", "P34_tipo_flangia", "P36_DN_out",
];

export default function LearningRuleModal({
  open,
  onOpenChange,
  voce,
  sessionContext,    // { cliente_email_domain, settore_code, ... }
  canCreateActive,   // true if titolare or super_admin
  onCreate,          // (rule_payload) => Promise<created_rule>
}) {
  const [step, setStep] = useState(1);
  const [overrides, setOverrides] = useState({});  // { P10_materiale: "AISI 304", ... }
  const [scope, setScope] = useState("articolo_listino");
  const [motivazione, setMotivazione] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset all state when opening
  React.useEffect(() => {
    if (open) {
      setStep(1);
      setOverrides({});
      setScope("articolo_listino");
      setMotivazione("");
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const scopeValue = useMemo(() => {
    if (!voce) return "";
    if (scope === "articolo_listino") {
      const cands = voce.candidati || [];
      return voce.codice_listino_proposed || cands[0]?.codice || "";
    }
    if (scope === "cliente") {
      return sessionContext?.cliente_email_domain || "";
    }
    if (scope === "settore") {
      return (
        unwrapParam(voce.parametri_estratti?.P21_settore) ||
        unwrapParam(voce.parametri_inferiti?.P21_settore) ||
        sessionContext?.settore_code ||
        ""
      );
    }
    return "";
  }, [scope, voce, sessionContext]);

  const canGoNext = useMemo(() => {
    if (step === 1) return Object.keys(overrides).length > 0;
    if (step === 2) return !!scopeValue;
    if (step === 3) return motivazione.trim().length >= 10;
    return true;
  }, [step, overrides, scopeValue, motivazione]);

  const handleNext = () => {
    if (step < 4) setStep(step + 1);
  };
  const handleBack = () => {
    if (step > 1) setStep(step - 1);
    setError(null);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const sotto = (voce?.sotto_tipo || "").trim();
      const keyword = sotto.split(/\s+/).slice(0, 2).join(" ") || sotto;
      const payload = {
        scope,
        scope_value: scopeValue,
        trigger_pattern: {
          keyword_match: keyword ? [keyword] : [],
          famiglia: voce?.famiglia || null,
        },
        output_overrides: overrides,
        motivazione: motivazione.trim(),
      };
      await onCreate(payload);
      onOpenChange(false);
    } catch (e) {
      setError(e.message || "Errore creazione regola");
    } finally {
      setSubmitting(false);
    }
  };

  if (!voce) return null;
  const estr = voce.parametri_estratti || {};
  const inf = voce.parametri_inferiti || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl"
        data-testid="learning-rule-modal"
      >
        <DialogHeader>
          <DialogTitle>
            {canCreateActive ? "Insegna su questa voce" : "Proponi una regola"}
            <span className="ml-2 text-xs text-muted-foreground">
              · Step {step}/4
            </span>
          </DialogTitle>
          <DialogDescription>
            {voce.sotto_tipo || "voce"}{voce.famiglia ? ` (${voce.famiglia})` : ""}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div data-testid="learning-rule-step-1">
            <div className="text-sm font-medium mb-2">Cosa correggere?</div>
            <p className="text-xs text-muted-foreground mb-3">
              Seleziona i campi e inserisci il valore corretto. La regola
              applicherà queste correzioni automaticamente per voci simili.
            </p>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-2">
              {PARAM_CANDIDATES.map((p) => {
                const current =
                  unwrapParam(estr[p]) ?? unwrapParam(inf[p]) ?? "—";
                const checked = p in overrides;
                return (
                  <div
                    key={p}
                    className="flex items-center gap-2 py-1 border-b border-slate-100"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) => {
                        setOverrides((prev) => {
                          const next = { ...prev };
                          if (v) {
                            next[p] = String(current === "—" ? "" : current);
                          } else {
                            delete next[p];
                          }
                          return next;
                        });
                      }}
                      data-testid={`learning-rule-field-${p}`}
                    />
                    <div className="flex-1 text-xs">
                      <span className="font-mono text-slate-500">{p}</span>{" "}
                      <span>{PARAM_LABELS[p] || p}</span>
                      <span className="ml-2 text-slate-400">attuale: {String(current)}</span>
                    </div>
                    <Input
                      disabled={!checked}
                      value={overrides[p] || ""}
                      onChange={(e) =>
                        setOverrides((prev) => ({ ...prev, [p]: e.target.value }))
                      }
                      placeholder="Nuovo valore…"
                      className="w-40 h-8 text-xs"
                      data-testid={`learning-rule-value-${p}`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="learning-rule-step-2">
            <div className="text-sm font-medium mb-2">A quali voci applicare?</div>
            <RadioGroup value={scope} onValueChange={setScope}>
              <div className="space-y-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <RadioGroupItem
                    value="articolo_listino"
                    data-testid="learning-rule-scope-articolo"
                  />
                  <div className="text-sm">
                    <div className="font-medium">Articolo listino</div>
                    <div className="text-xs text-slate-500">
                      Codice:{" "}
                      <span className="font-mono">
                        {voce.codice_listino_proposed ||
                          (voce.candidati || [])[0]?.codice ||
                          "—"}
                      </span>
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <RadioGroupItem
                    value="cliente"
                    data-testid="learning-rule-scope-cliente"
                  />
                  <div className="text-sm">
                    <div className="font-medium">Cliente</div>
                    <div className="text-xs text-slate-500">
                      Dominio:{" "}
                      <span className="font-mono">
                        {sessionContext?.cliente_email_domain || "—"}
                      </span>
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <RadioGroupItem
                    value="settore"
                    data-testid="learning-rule-scope-settore"
                  />
                  <div className="text-sm">
                    <div className="font-medium">Settore</div>
                    <div className="text-xs text-slate-500">
                      Settore:{" "}
                      <span className="font-mono">
                        {unwrapParam(voce.parametri_estratti?.P21_settore) ||
                          unwrapParam(voce.parametri_inferiti?.P21_settore) ||
                          "—"}
                      </span>
                    </div>
                  </div>
                </label>
              </div>
            </RadioGroup>
            {!scopeValue && (
              <div className="mt-3 text-xs text-amber-700">
                ⚠️ Valore scope non disponibile. Scegli un altro scope.
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div data-testid="learning-rule-step-3">
            <Label className="text-sm font-medium">Motivazione (obbligatoria)</Label>
            <p className="text-xs text-muted-foreground mb-2 mt-1">
              Spiega brevemente perché questa correzione si applica al{" "}
              {scope === "articolo_listino"
                ? "codice listino"
                : scope === "cliente"
                ? "cliente"
                : "settore"}{" "}
              <span className="font-mono">{scopeValue}</span>.
            </p>
            <Textarea
              value={motivazione}
              onChange={(e) => setMotivazione(e.target.value)}
              placeholder="Es: I collari standard sono in AISI 304, non 316 (richiesta cliente)..."
              rows={4}
              data-testid="learning-rule-motivazione"
            />
            <div className="text-xs text-slate-400 mt-1">
              {motivazione.trim().length} / 10 minimi
            </div>
          </div>
        )}

        {step === 4 && (
          <div data-testid="learning-rule-step-4">
            <div className="text-sm font-medium mb-3">Preview regola</div>
            <div className="rounded border bg-slate-50 p-3 text-xs space-y-2">
              <div>
                <span className="text-slate-500">Quando vedrò voci simili in:</span>{" "}
                <Badge variant="outline">
                  {scope === "articolo_listino"
                    ? "Articolo listino"
                    : scope === "cliente"
                    ? "Cliente"
                    : "Settore"}{" "}
                  · {scopeValue}
                </Badge>
              </div>
              <div className="text-slate-500">Applicherò:</div>
              <ul className="ml-4 list-disc">
                {Object.entries(overrides).map(([k, val]) => (
                  <li key={k}>
                    <span className="font-mono">{k}</span> →{" "}
                    <span className="font-medium">{val}</span>
                  </li>
                ))}
              </ul>
              <div className="border-t pt-2 mt-2">
                <span className="text-slate-500">Motivazione:</span>{" "}
                <em>"{motivazione}"</em>
              </div>
            </div>
            {!canCreateActive && (
              <div className="mt-3 text-xs text-amber-700">
                ℹ️ La regola verrà creata in stato <strong>pending_approval</strong>{" "}
                e dovrà essere approvata da un titolare/super_admin prima di
                essere applicata.
              </div>
            )}
            {error && (
              <div className="mt-3 text-xs text-red-700" data-testid="learning-rule-error">
                {error}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {step > 1 && (
            <Button variant="outline" onClick={handleBack} disabled={submitting}>
              Indietro
            </Button>
          )}
          {step < 4 && (
            <Button
              onClick={handleNext}
              disabled={!canGoNext}
              data-testid="learning-rule-next-btn"
            >
              Avanti
            </Button>
          )}
          {step === 4 && (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="learning-rule-submit-btn"
            >
              {submitting ? "Salvataggio…" : canCreateActive ? "Crea regola" : "Proponi regola"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
